import React from 'react';
import { Sparkles, Headphones, Bot, Target, ArrowRight } from 'lucide-react';

/**
 * MemberEcosystem
 *
 * A 4-card strip that shows the membership ladder on the homepage AFTER
 * the quiz pitch. The Free tier sits first (entry point), then the three
 * paid upsells in ascending commitment. Copy is intentionally short —
 * the homepage is a gateway, not a pricing page.
 *
 * Naming decision: free tier is called "Mental Edge Member" so the
 * narrative carries from "get your Mental Edge Score" → "become a
 * Mental Edge Member."
 */
const TIERS = [
  {
    eyebrow: 'Step 1 · Free',
    title: 'Mental Edge Member',
    body: 'Take the quiz, get your score + custom plan, and unlock weekly mental-game emails.',
    icon: Sparkles,
    href: '/quiz',
    cta: 'Start free',
    testid: 'eco-card-free',
    accent: 'from-[#0a0d0e] to-[#0a0d0e]',
    border: 'border-[#4FBCC7]/30 hover:border-[#4FBCC7]/60',
    badge: '#4FBCC7',
  },
  {
    eyebrow: 'Step 2 · $27',
    title: 'Athlete IDP',
    body: 'Your full 6-dimension scored profile, primary action, and printable PDF.',
    icon: Target,
    href: '/assessment',
    cta: 'Get the IDP',
    testid: 'eco-card-idp',
    accent: 'from-[#0a0d0e] to-[#0a0d0e]',
    border: 'border-white/10 hover:border-white/25',
    badge: '#A5ACAF',
  },
  {
    eyebrow: 'Step 3 · Member',
    title: 'The Locker Room',
    body: 'Pre-game playlists, audio drops, and the Apex Six notecard library.',
    icon: Headphones,
    href: '/locker-room',
    cta: 'Open the locker',
    testid: 'eco-card-locker',
    accent: 'from-[#0a0d0e] to-[#0a0d0e]',
    border: 'border-white/10 hover:border-white/25',
    badge: '#A5ACAF',
  },
  {
    eyebrow: 'Step 4 · Member+',
    title: 'Apex AI Coach',
    body: 'A coach in your pocket — pre-game plans, journal reflections, in Coach Lewis’s voice.',
    icon: Bot,
    href: '/app/signup',
    cta: 'Try free',
    testid: 'eco-card-ai',
    accent: 'from-[#0a0d0e] to-[#0a0d0e]',
    border: 'border-white/10 hover:border-white/25',
    badge: '#A5ACAF',
  },
];

export const MemberEcosystem = () => {
  return (
    <section
      id="member-ecosystem"
      className="relative bg-gradient-to-b from-[#0a0d0e] to-[#0f1416] py-14 sm:py-18"
      data-testid="member-ecosystem"
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
        <div className="text-center mb-10">
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] font-bold text-[#4FBCC7] mb-3">
            How it scales with you
          </div>
          <h2 className="font-heading text-[1.8rem] sm:text-[2.3rem] font-bold tracking-tight text-white leading-[1.15] max-w-[40ch] mx-auto">
            Free members get the score. <span className="text-[#4FBCC7]">Paid members get the system.</span>
          </h2>
          <p className="mt-4 text-[14px] sm:text-[15px] text-white/60 max-w-[56ch] mx-auto leading-relaxed">
            Start with the free Mindset Quiz. Add the pieces that fit your season — the IDP, the Locker Room, or the AI Coach — when you&rsquo;re ready.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => {
            const TIcon = t.icon;
            return (
              <a
                key={t.title}
                href={t.href}
                data-testid={t.testid}
                className={`group rounded-2xl border-2 ${t.border} bg-white/[0.03] p-5 transition-all duration-200 hover:-translate-y-1 hover:bg-white/[0.05]`}
              >
                <div
                  className="font-mono-accent uppercase tracking-[0.2em] text-[10px] font-bold mb-3"
                  style={{ color: t.badge }}
                >
                  {t.eyebrow}
                </div>
                <div className="w-11 h-11 rounded-xl bg-[#004C54]/25 border border-[#4FBCC7]/25 flex items-center justify-center mb-3">
                  <TIcon className="h-5 w-5 text-[#4FBCC7]" />
                </div>
                <h3 className="font-heading text-[1.05rem] font-bold tracking-tight text-white mb-1.5">
                  {t.title}
                </h3>
                <p className="text-[12.5px] text-white/60 leading-relaxed mb-4">
                  {t.body}
                </p>
                <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#4FBCC7] group-hover:text-white transition-colors">
                  {t.cta} <span aria-hidden="true">→</span>
                </span>
              </a>
            );
          })}
        </div>

        {/* Full-pricing footer — closes the loop so visitors scrolling the
            ladder can jump to the consolidated /pricing page (covers 1:1
            coaching, team workshops, and yearly subscription options that
            aren't on the homepage cards). */}
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-center">
          <span className="text-[13px] text-white/55">
            Want the full picture &mdash; 1:1 packages, teams &amp; schools, yearly options?
          </span>
          <a
            href="/pricing"
            data-testid="eco-see-full-pricing"
            onClick={() => {
              try {
                if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                  window.gtag('event', 'member_ecosystem_pricing_click', { label: 'See full pricing' });
                }
              } catch {
                /* analytics is best-effort */
              }
            }}
            className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#4FBCC7] hover:text-white underline decoration-[#4FBCC7]/40 decoration-2 underline-offset-[5px] hover:decoration-white transition-colors"
          >
            See full pricing
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default MemberEcosystem;
